import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { supabaseAnon } from './supabaseAnon';
import { PublicNotFound } from './PublicNotFound';

/**
 * Public-safe column list (EVT-05).
 *
 * The supabaseAnon client only ever sees rows allowed by Plan 04's
 * `anon_read_public_events` policy, but this column whitelist is the
 * second guarantee: even if a future RLS change widens what an anon
 * caller can read, the SELECT projection here cannot ever return
 * forbidden columns. Mirrors the `01-RESEARCH.md` §"Public-Safe Field
 * Set" table exactly.
 */
const PUBLIC_SAFE_COLS =
    'id, title, description, event_type, start_date, end_date, primary_location_text';

interface PublicEvent {
    id: string;
    title: string;
    description: string | null;
    event_type: string;
    start_date: string;
    end_date: string;
    primary_location_text: string | null;
}

type LoadState = 'loading' | 'notfound' | PublicEvent;

/**
 * Compute the temporal label shown in the type-chip row. Day-level
 * comparison; matches the rule cited in `01-RESEARCH.md` §"Public-Safe
 * Field Set" -> `Temporal label`.
 */
function temporalLabel(
    start: string,
    end: string
): 'upcoming' | 'current' | 'past' {
    const today = new Date().toISOString().split('T')[0];
    if (today < start) return 'upcoming';
    if (today > end) return 'past';
    return 'current';
}

/**
 * `PublicEventDetail` — `/events/:id` route (D-12).
 *
 * Mounted ABOVE the admin-framework `<Admin>` tree so this component
 * never touches the auth-provider chain (Pitfall 5). Reads exclusively
 * through the standalone anon-key Supabase client.
 *
 * 404 rule: identical `<PublicNotFound />` render for every failure
 * mode (missing id / internal / cancelled / archived). Spec
 * `pages/public/event-detail.md` §4 + threat model T-08-S3 forbid
 * leaking which condition triggered the not-found.
 */
export const PublicEventDetail = () => {
    const { id } = useParams<{ id: string }>();
    const [state, setState] = useState<LoadState>('loading');

    useEffect(() => {
        if (!id) {
            setState('notfound');
            return;
        }
        // Defense-in-depth: the same RLS-mirroring predicates as
        // `PublicCalendar`. RLS is the truth (D-17), but if a future
        // dataProvider mistake bypasses RLS, this whitelist still keeps
        // the route private.
        supabaseAnon
            .from('events')
            .select(PUBLIC_SAFE_COLS)
            .eq('id', id)
            .eq('visibility', 'public')
            .eq('status', 'scheduled')
            .eq('is_active', true)
            .maybeSingle()
            .then(({ data }) => {
                if (!data) {
                    // Identical 404 regardless of *why* the row was
                    // missing. Do not branch on whether the id existed,
                    // whether it was internal, cancelled, or archived
                    // — all four collapse to the same UX.
                    setState('notfound');
                } else {
                    setState(data as PublicEvent);
                }
            });
    }, [id]);

    if (state === 'loading') {
        return <Typography color="text.secondary">Loading…</Typography>;
    }
    if (state === 'notfound') {
        return <PublicNotFound />;
    }

    const event = state;
    const label = temporalLabel(event.start_date, event.end_date);
    const dateText =
        event.start_date === event.end_date
            ? event.start_date
            : `${event.start_date} → ${event.end_date}`;

    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip
                    label={label}
                    color={
                        label === 'upcoming'
                            ? 'primary'
                            : label === 'current'
                              ? 'success'
                              : 'default'
                    }
                />
                <Chip label={event.event_type} variant="outlined" />
            </Stack>
            <Typography variant="h1" component="h1">
                {event.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {dateText}
            </Typography>
            {event.primary_location_text && (
                <Typography variant="body1">
                    {event.primary_location_text}
                </Typography>
            )}
            {event.description && (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {event.description}
                </Typography>
            )}
            <Box pt={4}>
                {/*
                 * Plan 09 ships the iCal feed; the Subscribe affordance
                 * is rendered prominently on `/calendar` so it is not
                 * duplicated here per `spec/pages/public/event-detail.md`
                 * §6.4 "Navigation and subscribe" — the page already
                 * lives inside the public layout that points users to
                 * `/calendar`. Plan 13 may reintroduce the Subscribe
                 * button on this page if branding wants it.
                 */}
            </Box>
        </Stack>
    );
};

export default PublicEventDetail;
